<template>
    <div>
        <slot name="activator" v-bind:show="doShowDialog" v-if="$scopedSlots.activator" />
        <v-btn v-else @click.stop="showDialog = true" text color="zoezi-edit-color">{{add ? $translate('Create new component') : $translate('Edit') + ' ' + (named ? name : label)}}</v-btn>
        <v-dialog v-model="showDialog" :persistent="changed" max-width="80vw" scrollable :fullscreen="$store.state.codeMaximizeWindow">
            <v-card height="80vh" ref="card">
                <v-card-title class="headline zoezi-edit-color">
                    {{add ? $translate('Create new component') : $translate('Edit') + ' ' + (named ? name : label)}}
                    <div class="ml-auto">
                        <v-btn icon @click="$store.commit('setCodeMaximizeWindow', !$store.state.codeMaximizeWindow); refreshEditors()" class="zoezi-edit-color"><v-icon>{{$store.state.codeMaximizeWindow ? 'mdi-window-restore' : 'mdi-window-maximize'}}</v-icon></v-btn>
                        <v-btn v-if="!changed" icon @click="showDialog = false" class="zoezi-edit-color"><v-icon>mdi-close</v-icon></v-btn>
                    </div>
                </v-card-title>
                <v-card-text>
                    <div v-if="hint" class="mt-2">
                        {{hint}}
                    </div>

                    <div v-if="named">
                        <v-row>
                            <v-col cols=2>
                                <icon-setting :label="$translate('Icon')" :value="icon" @input="icon = $event" :clearable="false" />
                            </v-col>
                            <v-col :cols="add ? 10 : 7">
                                <v-text-field :label="$translate('Name')" color="zoezi-edit-color" v-model="name" />
                            </v-col>
                            <v-col v-if="!add" cols=2>
                                &lt;{{id}}&nbsp;/&gt;
                            </v-col>
                            <v-col v-if="!add" cols=1>
                                <v-btn color="error" @click="showDialog = false; $emit('remove', emitValue)">{{$translate('Remove')}}</v-btn>
                            </v-col>
                        </v-row>
                    </div>

                    <!-- Dialog for creating an own component-->
                    <v-dialog v-model="showCreateOwnComponentDialog" max-width="500px" v-if="showCreateOwnComponentDialog">
                        <v-card>
                            <v-card-title class="zoezi-edit-color headline align-start flex-column pa-2">
                                <div class="font-weight-bold fill-width">
                                    {{$translate('Convert to reusable component')}}
                                    <v-btn icon @click="showCreateOwnComponentDialog = false" class="float-right zoezi-edit-color"><v-icon>mdi-close</v-icon></v-btn>
                                </div>
                            </v-card-title>
                            <v-card-text>
                                <div class="my-4">
                                    {{$translate('You can convert this component to a "Own component" so that it can be used in multiple places on the website.')}}
                                </div>
                                <icon-setting :label="$translate('Icon')" :value="icon" @input="icon = $event" :clearable="false" />
                                <v-text-field :label="$translate('Name')" color="zoezi-edit-color" v-model="name" />
                                <v-btn color="zoezi-edit-color" @click="convertToOwnComponent" :disabled="!icon || !name">{{$translate('Convert to reusable component')}}</v-btn>
                            </v-card-text>
                        </v-card>
                    </v-dialog>

                    <div v-if="isDefault || add" class="d-flex">
                        <v-text-field v-if="$api.hasFlag('flag-ai-component')" label="Beskriv vad du vill skapa" color="zoezi-edit-color" v-model="aIprompt" @keyup.enter="createUsingAi" :loading="aiLoading" :disabled="aiLoading" class="fill-width flex-grow-1">
                            <template v-slot:append>
                                <v-btn icon @click="createUsingAi" :loading="aiLoading">
                                    <v-icon>mdi-send-circle-outline</v-icon>
                                </v-btn>
                            </template>
                        </v-text-field>
                        <v-spacer/>
                        <div v-if="component" class="ml-2 mt-4">
                            <v-btn @click="getSuggestion" color="zoezi-edit-color" :loading="suggestionLoading">{{$translate('Convert to reusable component')}}</v-btn>
                        </div>
                    </div>
                    <div v-else class="d-flex">
                        <v-text-field v-if="$api.hasFlag('flag-ai-component')" label="Beskriv vad du vill ändra" color="zoezi-edit-color" v-model="aIprompt" @keyup.enter="changeUsingAi" :loading="aiLoading" :disabled="aiLoading" class="fill-width flex-grow-1">
                            <template v-slot:append>
                                <v-btn icon @click="changeUsingAi" :loading="aiLoading">
                                    <v-icon>mdi-send-circle-outline</v-icon>
                                </v-btn>
                            </template>
                        </v-text-field>
                        <v-spacer/>
                        <div v-if="component" class="ml-2 mt-4">
                            <v-btn @click="getSuggestion" color="zoezi-edit-color" :loading="suggestionLoading">{{$translate('Convert to reusable component')}}</v-btn>
                        </div>
                    </div>

                    <v-tabs color="zoezi-edit-color" @change="tabChange">
                        <v-tab v-if="!cssOnly">{{$translate('HTML')}}</v-tab>
                        <v-tab v-if="!htmlOnly && !cssOnly">{{$translate('JavaScript')}}</v-tab>
                        <v-tab v-if="!htmlOnly">{{$translate('CSS')}}</v-tab>

                        <v-tab-item v-if="!cssOnly">
                            <codemirror
                                v-model="html"
                                :merge="showDiffEditor"
                                :options="cmOptionHtmlPlusMerge"
                                @input="testCompileTemplate($event)"
                                class="zoezi-vue-code-setting-cm"
                                :style="{height: editorHeight + 'px'}"
                                ref="html"
                                @focus="testCompileTemplate(html); refreshEditor($refs.html)"
                                @keydown.ctrl.83.prevent.stop="save"
                                :key="'html' + showDiffEditor"
                            />
                            <div style="color: red; white-space: pre-line" v-text="templateError" />
                        </v-tab-item>

                        <v-tab-item v-if="!htmlOnly && !cssOnly">
                            <codemirror
                                v-model="js"
                                :merge="showDiffEditor"
                                :options="cmOptionJsPlusMerge"
                                class="zoezi-vue-code-setting-cm"
                                :style="{height: editorHeight + 'px'}"
                                ref="js"
                                @focus="refreshEditor($refs.js)"
                                @keydown.ctrl.83.prevent.stop="save"
                                :key="'js' + showDiffEditor"
                            />
                        </v-tab-item>

                        <v-tab-item v-if="!htmlOnly">
                            <codemirror
                                v-model="css"
                                :merge="showDiffEditor"
                                :options="cmOptionCssPlusMerge"
                                ref="css"
                                class="zoezi-vue-code-setting-cm"
                                :style="{height: editorHeight + 'px'}"
                                @focus="refreshEditor($refs.css)"
                                @keydown.ctrl.83.prevent.stop="save"
                                :key="'css' + showDiffEditor"
                            />
                        </v-tab-item>
                    </v-tabs>
                    
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-checkbox v-model="showDiffEditor" :label="$translate('Show changes')" color="zoezi-edit-color" class="mr-2" />
                    <v-btn @click="save" color="zoezi-edit-color" :disabled="!changed || !requiredValuesEntered">{{$translate('Save')}}</v-btn>
                    <v-btn @click="showDialog = false">{{$translate('Close')}}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
const compiler = require('vue-template-compiler')
import VueComponent from '../views/VueComponent.vue'
import {getRandomId} from '../util.js'
import IconSetting from './IconSetting.vue'
import Vue from 'vue'
import components from '../component.js';
export default {
    name: 'zoezi-vue-code-setting',

    props: {
        value: {
        },
        label: {
            type: String
        },
        open: {
            type: Boolean
        },
        add: {
            type: Boolean
        },
        named: {
            type: Boolean
        },
        htmlOnly: {
            type: Boolean,
            default: false
        },
        cssOnly: {
            type: Boolean,
            default: false
        },
        hint: {},
        requireSingleTag: {
            type: Boolean,
            default: true
        },
        component: {}
    },

    components: {
        IconSetting
    },

    watch: {
        value: {
            immediate: true,
            handler(value) {
                this.setValue(value);
            }
        },
        showDialog(show) {
            if (show) {
                this.aIprompt = '';
                this.showDiffEditor = false;
                this.templateError = '';
                this.setValue(this.value);
                this.refreshEditors();
                this.$emit('open');
                this.enableCtrlS();
            } else {
                this.disableCtrlS();
            }
        },
        open: {
            immediate: true,
            handler(open) {
                if (open) {
                    this.showDialog = true;
                }
            }
        },
        js: {
            immediate: true,
            handler(js) {
                if (js == undefined) {
                    this.js = this.value?.js || '';
                }
            }
        },
        html: {
            immediate: true,
            handler(html) {
                if (html == undefined) {
                    this.html = this.value?.html || '';
                }
            }
        },
        css: {
            immediate: true,
            handler(css) {
                if (css == undefined) {
                    this.css = this.value?.css || '';
                }
            }
        }
    },

    computed: {
        emitValue() {
            let value = {html: this.html, js: this.js, css: this.css};
            if (this.named) {
                value.name = this.name;
                value.id = this.id;
                value.icon = this.icon;
            }
            if (this.htmlOnly) {
                value = this.html;
            }
            if (this.cssOnly) {
                value = this.css;
            }
            return value;
        },
        changed() {
            if (this.htmlOnly) {
                return this.value != this.html;
            }
            if (this.cssOnly) {
                return this.value != this.css;
            }
            return this.value?.html != this.html || this.value?.js != this.js || this.value?.css != this.css || (this.named && (this.value?.name != this.name || this.value?.icon != this.icon));
        },
        isDefault() {
            return JSON.stringify(VueComponent.props.vue.default()) == JSON.stringify(this.value);
        },
        requiredValuesEntered() {
            return !this.named || (this.name?.length && this.id?.length && this.icon?.length);
        },
        cmOptionHtmlPlusMerge() {
            if (this.showDiffEditor) {
                const mergeOptions = {
                    value: this.html,
                    origLeft: null,
                    orig: this.value?.html,
                    connect: 'align'
                };

                return {
                    ...this.cmOptionHtml,
                    ...mergeOptions
                };
            }
            return this.cmOptionHtml;
        },
        cmOptionJsPlusMerge() {
            if (this.showDiffEditor) {
                const mergeOptions = {
                    value: this.js,
                    origLeft: null,
                    orig: this.value?.js,
                    connect: 'align'
                };

                return {
                    ...this.cmOptionJs,
                    ...mergeOptions
                };
            }
            return this.cmOptionJs;
        },
        cmOptionCssPlusMerge() {
            if (this.showDiffEditor) {
                const mergeOptions = {
                    value: this.css,
                    origLeft: null,
                    orig: this.value?.css,
                    connect: 'align'
                };

                return {
                    ...this.cmOptionCss,
                    ...mergeOptions
                };
            }
            return this.cmOptionCss;
        }
    },
    data: () => ({
        showDialog: false,
        name: '',
        id: '',
        html: '',
        js: '',
        css: '',
        icon: '',
        templateError: '',
        aIprompt: '',
        aiLoading: false,
        suggestionLoading: false,
        cmOptionHtml: {
            tabSize: 4,
            indentUnit: 4,
            matchBrackets: true,
            mode: 'text/html',
            readOnly: false,
            lineNumbers: true,
            lineWrapping: true,
            foldGutter: true,
            gutters: ["CodeMirror-linenumbers", "CodeMirror-foldgutter"]
        },
        cmOptionJs: {
            tabSize: 4,
            indentUnit: 4,
            matchBrackets: true,
            mode: 'text/javascript',
            readOnly: false,
            lineNumbers: true,
            lineWrapping: true,
            foldGutter: true,
            lint: {
                esversion: 11,
                asi: true
            },
            gutters: ["CodeMirror-linenumbers", "CodeMirror-foldgutter", "CodeMirror-lint-markers"]
        },
        cmOptionCss: {
            tabSize: 4,
            indentUnit: 4,
            matchBrackets: true,
            mode: 'text/css',
            readOnly: false,
            lineNumbers: true,
            lineWrapping: true,
            foldGutter: true,
            gutters: ["CodeMirror-linenumbers", "CodeMirror-foldgutter"]
        },
        showDiffEditor: false,
        showCreateOwnComponentDialog: false,
        editorHeight: 300
    }),

    methods: {
        save() {
            if (this.changed && this.requiredValuesEntered) {
                this.$emit('input', this.emitValue);
            }
        },
        testCompileTemplate(code) {
            if (this.cssOnly) {
                this.templateError = null;
                return;
            }

            let compiled = compiler.compile(code);
            let errors = compiled.errors;
            if (errors && !this.requireSingleTag) {
                errors = errors.filter(x => x != 'Component template should contain exactly one root element. If you are using v-if on multiple elements, use v-else-if to chain them instead.')
            }
            if (errors && errors.length) {
                this.templateError = errors.join('\n')
            } else {
                this.templateError = null;
            }
        },
        refreshEditors() {
            setTimeout(() => {
                this.refreshEditor(this.$refs.js)
                this.refreshEditor(this.$refs.html)
                this.refreshEditor(this.$refs.css)
            }, 500)
        },
        refreshEditor(editor) {
            if (this.$refs?.card?.$el) {
                const r = this.$refs.card.$el.getBoundingClientRect();
                this.editorHeight = r.height - 290;
            }

            if (editor && editor.codemirror && editor.codemirror.refresh) {
                editor.codemirror.refresh();
            }
        },
        doShowDialog() {
            this.showDialog = true;
        },
        enableCtrlS() {
            document.addEventListener('keydown', this.ctrlSSave);
        },
        disableCtrlS() {
            document.removeEventListener('keydown', this.ctrlSSave);
        },
        beforeDestroy() {
            this.disableCtrlS();
        },
        ctrlSSave(e) {
            if (!(e.keyCode === 83 && e.ctrlKey)) {
                return;
            }
            e.preventDefault();
            this.save();
        },
        setValue(value) {
            if (value) {
                this.name = this.named ? value.name : '';
                this.id = this.named ? value.id : '';
                this.icon = this.named ? value.icon : '';
                if (this.htmlOnly) {
                    this.js = '';
                    this.html = value || '';
                    this.css = '';
                } else if (this.cssOnly) {
                    this.js = '';
                    this.html = '';
                    this.css = value || '';
                } else {
                    this.js = value.js || '';
                    this.html = value.html || '';
                    this.css = value.css || '';
                }
            } else {
                this.name = '';
                this.id = '';
                this.icon = '';
                this.js = '';
                this.html = '';
                this.css = '';
            }

            if (!this.js.startsWith('export default ')) {
                this.js = 'export default ' + this.js.trimStart();
            }

            if (this.add && !this.icon) {
                this.icon = 'mdi-new-box';
            }

            if (this.named && !this.id) {
                this.id = `zoezi-${getRandomId(16)}`;
            }

            this.refreshEditors();
        },
        createUsingAi() {
            this.aiLoading = true;
            this.$api.post('/api/homepage/component/ai/create', {prompt: this.aIprompt}).then(response => {
                this.aiLoading = false;
                response.json().then(data => {
                    this.html = data.html;
                    this.js = data.js;
                    this.css = data.css;
                    this.aIprompt = '';
                    this.$store.commit('showErrorDialog', {title: 'Komponent skapad', text: data.info, color: 'zoezi-edit-color'})
                    this.refreshEditors();
                });
            });
        },
        changeUsingAi() {
            this.aiLoading = true;
            this.$api.post('/api/homepage/component/ai/change', {prompt: this.aIprompt, component: {html: this.html, js: this.js, css: this.css}}).then(response => {
                this.aiLoading = false;
                response.json().then(data => {
                    this.html = data.html;
                    this.js = data.js;
                    this.css = data.css;
                    this.aIprompt = '';
                    this.$store.commit('showErrorDialog', {title: 'Ändring genomförd', text: data.info, color: 'zoezi-edit-color'})
                    this.refreshEditors();
                });
            });
        },
        convertToOwnComponent() {
            const c = {html: this.html, js: this.js, css: this.css, id: `zoezi-${getRandomId(16)}`, name: this.name, icon: this.icon};
            this.showCreateOwnComponentDialog = false;
            if (!this.$root.siteconfig.ownComponents) {
                Vue.set(this.$root.siteconfig, 'ownComponents', {})
            }

            let p = this.component;
            while (p && p.$options.name != 'ComponentWrapper') {
                p = p.$parent;
            }
            p.config.props = {};
            p.config.component = c.id;

            this.$root.siteconfig.ownComponents[c.id] = c;
            this.$root.saveHomePage();
            components.updateOwnComponents(this.$root.siteconfig);
            components.updateAllLiveComponents(c.id);
        },
        getSuggestion() {
            this.suggestionLoading = true;
            const showDialog = (name, icon) => {
                this.name = name;
                this.icon = icon;
                this.showCreateOwnComponentDialog = true
                this.suggestionLoading = false;
            }

            if (!this.$api.hasFlag('flag-ai-component')) {
                showDialog('', null);
                return;
            }

            this.$api.post('/api/homepage/component/ai/suggest_name', {component: {html: this.html, js: this.js, css: this.css}}).then(response => {
                if (!response.ok) {
                    showDialog('', null);
                    return;
                }

                response.json().then(data => {
                    showDialog(data.name, data.icon);
                });
            });
        },
        tabChange() {
            this.refreshEditors();
        }
    }
  }
</script>